import React, { useEffect, useState } from "react";
import {
  useModalOldContext,
  useWSModal,
  WSElement,
  WSFlexBox,
  WSLoader,
  WSModalOld,
  WSText
} from "@wingspanhq/fe-component-library";

import {
  useMutationCreateIntegrationsQuickbooksAccountAssets,
  useMutationCreateIntegrationsQuickbooksAccountExpenses,
  useMutationCreateIntegrationsQuickbooksAccountLiabilities,
  useMutationCreateIntegrationsQuickbooksAccountRevenues,
  useMutationCreateIntegrationsQuickbooksCustomers,
  useMutationCreateIntegrationsQuickbooksItems,
  useMutationCreateIntegrationsQuickbooksVendors,
  useMutationUpdateIntegrationQuickbooks
} from "../../../../query/integrations/mutations";
import { IntegrationsQuickbooksSyncStatus } from "@wingspanhq/integrations/dist/lib/interfaces/quickbooks-api/integrationsQuickbooks";

export const INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT =
  "INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT";

export enum QUICKBOOKS_ENTITY {
  CUSTOMERS = "CUSTOMERS",
  VENDORS = "VENDORS",
  ITEMS = "ITEMS",
  EXPENSES = "EXPENSES",
  ACCOUNTS = "ACCOUNTS"
}

const entityToNameMap = {
  [QUICKBOOKS_ENTITY.CUSTOMERS]: "Customers",
  [QUICKBOOKS_ENTITY.VENDORS]: "Vendors",
  [QUICKBOOKS_ENTITY.ITEMS]: "Items",
  [QUICKBOOKS_ENTITY.EXPENSES]: "Expense Accounts",
  [QUICKBOOKS_ENTITY.ACCOUNTS]: "Accounts"
};

export const IntegrationsQuickbooksResyncSelectModal: React.FC<{
  entity: QUICKBOOKS_ENTITY;
}> = ({ entity }) => {
  const { closeModal } = useModalOldContext();

  const [createIntegrationsQuickbooksAccountAssets] =
    useMutationCreateIntegrationsQuickbooksAccountAssets();
  const [createIntegrationsQuickbooksAccountExpenses] =
    useMutationCreateIntegrationsQuickbooksAccountExpenses();
  const [createIntegrationsQuickbooksAccountLiabilities] =
    useMutationCreateIntegrationsQuickbooksAccountLiabilities();
  const [createIntegrationsQuickbooksCustomers] =
    useMutationCreateIntegrationsQuickbooksCustomers();
  const [createIntegrationsQuickbooksItems] =
    useMutationCreateIntegrationsQuickbooksItems();
  const [createIntegrationsQuickbooksVendors] =
    useMutationCreateIntegrationsQuickbooksVendors();

  const [currentStep, setCurrentStep] = useState("Starting resync");

  const doSync = async () => {
    setCurrentStep("Beginning sync");

    // await updateIntegration({
    //   syncStatus: IntegrationsQuickbooksSyncStatus.PendingSync
    // });

    if (entity === QUICKBOOKS_ENTITY.CUSTOMERS) {
      setCurrentStep("Syncing customers");
      await createIntegrationsQuickbooksCustomers();
    } else if (entity === QUICKBOOKS_ENTITY.VENDORS) {
      setCurrentStep("Syncing vendors");
      await createIntegrationsQuickbooksVendors();
    } else if (entity === QUICKBOOKS_ENTITY.EXPENSES) {
      setCurrentStep("Syncing expense accounts");
      await createIntegrationsQuickbooksAccountExpenses();
    } else if (entity === QUICKBOOKS_ENTITY.ITEMS) {
      setCurrentStep("Syncing items");
      await createIntegrationsQuickbooksItems();
    } else if (entity === QUICKBOOKS_ENTITY.ACCOUNTS) {
      setCurrentStep("Syncing asset accounts");
      await createIntegrationsQuickbooksAccountAssets();
      setCurrentStep("Syncing liability accounts");
      await createIntegrationsQuickbooksAccountLiabilities();
    }

    closeModal(INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT);
  };

  useEffect(() => {
    doSync();
  }, []);

  return (
    <>
      <WSText.Heading5
        mb="XL"
        data-testid="ReSyncQboAccountsModal"
      >{`Resyncing your QBO ${entityToNameMap[entity]} 🔧`}</WSText.Heading5>
      <WSText mb="3XL">
        We're syncing Wingspan with your Quickbooks. This should only take a
        moment.
      </WSText>

      <WSElement mb="XL">
        <WSLoader.Spinner mb="M" />
        <WSFlexBox justify="center">
          <WSText.ParagraphXs>{currentStep}</WSText.ParagraphXs>
        </WSFlexBox>
      </WSElement>
    </>
  );
};

export const RefreshModal: React.FC = () => {
  return (
    <>
      <WSModalOld
        blockClose={true}
        showCloseIcon={false}
        name={INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT}
      >
        {({ entity }) => (
          <IntegrationsQuickbooksResyncSelectModal entity={entity} />
        )}
      </WSModalOld>
    </>
  );
};

// Refactored version of RefreshModal
export interface QBORefreshModalProps {
  entity: QUICKBOOKS_ENTITY;
}

export const QBORefreshModal: React.FC<QBORefreshModalProps> = ({ entity }) => {
  return <IntegrationsQuickbooksResyncSelectModal entity={entity} />;
};

export function useQboRefreshModal() {
  return useWSModal(QBORefreshModal, {
    blockClose: true,
    showCloseIcon: false
  });
}
